/**
 * The API's wire code for "this slot cannot be priced", and the single
 * predicate that recognises it.
 *
 * WHY THIS EXISTS — DASHBOARD-3Y / DASHBOARD-3R
 * ---------------------------------------------
 * `GET payments/admin-bookings/price` answers **404** when a court carries no
 * pricing row for the requested sport, duration and capacity. The API is
 * explicit that this is not a malformed request — see
 * `api/src/court-pricing/unpriceable-booking.exception.ts`, which calls it "an
 * ordinary state of a live venue" and gave it the code below *precisely* so
 * that a caller could tell it apart from a missing row of any other kind.
 *
 * The dashboard received that code and ignored it, in two places, and both
 * read the STATUS instead:
 *
 * 1. `queryErrorReporting.ts` captures every non-401/403 failure of a read, on
 *    the reasoning that "nobody asked for anything: the client composed the
 *    URL, so a 4xx means the client asked wrong". That premise is true of the
 *    defect class that module was written for (a route the API does not have,
 *    a `NaN` in a path segment) and FALSE here: the operator chose the court,
 *    the sport and the capacity, and the API's answer is about the venue's
 *    data, not about the request. So a venue with an unpriced slot filed a
 *    dashboard defect every time an operator opened the booking modal on it,
 *    while the API itself drops its own 4xx (`sentryBeforeSend`).
 *
 * 2. `CreateAdminBookingModal` told the operator "Aucun prix pour N joueurs sur
 *    ce créneau — vérifie la tarification du terrain." on `isError`, i.e. for
 *    EVERY failure of that read. A 500, an aborted request or a dropped
 *    connection therefore sent them to audit pricing rules that were fine. A
 *    message that names a cause must be gated on the cause.
 *
 * Keyed on the CODE and nothing else: not the status (404 is shared with the
 * client-bug class above) and not the message (which is an English sentence
 * the API is free to reword, and which `unpriceable-booking.exception.ts`
 * itself says exists only for byte-for-byte backwards compatibility).
 */
import { ApiError } from '../apiClient';

/**
 * Mirrors `BOOKING_NOT_PRICEABLE_CODE` in
 * `api/src/court-pricing/unpriceable-booking.exception.ts`. It is not a DTO, so
 * `dto:sync` does not carry it — `unpriceableBooking.test.ts` pins this copy
 * against the API source so the two cannot drift silently.
 */
export const BOOKING_NOT_PRICEABLE_CODE = 'BOOKING_NOT_PRICEABLE';

/**
 * True when the API refused because the slot has no price at the requested
 * sport/duration/capacity — a venue-configuration state only an operator can
 * resolve, and the one 4xx on a read that is not a client bug.
 */
export function isUnpriceableBookingError(error: unknown): boolean {
    return (
        error instanceof ApiError && error.code === BOOKING_NOT_PRICEABLE_CODE
    );
}
